import React from "react";
import { Box } from "@mui/material";
import { IconCheck, IconInfo } from "../../utils/icons";

export interface JsonLdValidation {
    eligible: boolean;
    errors: { field: string; reason: string }[];
    warnings: { field: string; reason: string }[];
}

const FIELD_LABELS: Record<string, string> = {
    name: "title",
    description: "description",
    thumbnailUrl: "thumbnail",
    uploadDate: "upload date",
    contentUrl: "video file URL",
    duration: "duration",
    "@type": "type",
    "@context": "context"
};

const label = (field: string) => FIELD_LABELS[field] || field;

export const eligibilitySummary = (validation?: JsonLdValidation | null) => {
    if (!validation) return "No JSON-LD generated yet";
    if (validation.eligible) return "Verified for Google video results";
    return `Not eligible for Google: missing ${validation.errors.map((e) => label(e.field)).join(", ")}`;
};

export function JsonLdEligibility({
    validation,
    size = "sm"
}: {
    validation?: JsonLdValidation | null;
    // "lg" for a section heading (the video's JSON-LD block); "sm" inline in lists.
    size?: "sm" | "lg";
}) {
    if (!validation) return null;

    const { eligible, warnings } = validation;
    const iconSize = size === "lg" ? 22 : 13;

    return (
        <Box className="flex flex-col gap-[4px]">
            <span
                className={`inline-flex items-center gap-[8px] ${
                    size === "lg" ? "text-[14px] font-semibold" : "text-[12.5px]"
                } ${eligible ? "text-[var(--up)]" : "text-[var(--down)]"}`}
            >
                {eligible ? (
                    <IconCheck width={iconSize} height={iconSize} stroke="currentColor" />
                ) : (
                    <IconInfo size={iconSize} stroke="currentColor" />
                )}
                {eligibilitySummary(validation)}
            </span>
            {warnings.length > 0 && (
                <span className="text-[11.5px] text-muted">
                    Improvable: {warnings.map((w) => `${label(w.field)} ${w.reason}`).join(", ")}
                </span>
            )}
        </Box>
    );
}
